// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// Piechart renderer for the SVG phylogenetic tree viewer.
//
// Piecharts appear ONLY on collapsed internal nodes. Each pie summarises the
// leaf-colour composition of the collapsed subtree hidden beneath that node.
// Angle convention: 0 = top (12 o'clock), increasing clockwise.

import { el, rgba } from "./dom.js";
import { getLeafDescendants } from "./annotations-geometry.js";
import { effectiveNodeSize } from "./node-scale.js";

const TWO_PI = Math.PI * 2;

// ---------------------------------------------------------------------------
// Slice data
// ---------------------------------------------------------------------------

function buildPieData(targetNodes) {
  let totalLeaves = 0;

  const nodeData = targetNodes.map((node) => {
    const leaves = getLeafDescendants(node);
    const numberOfActiveLeaves = leaves.length;

    // Group leaves by fillColour, preserving first-encounter order.
    const sliceMap = new Map();
    for (const leaf of leaves) {
      const colour = leaf.fillColour;
      // Use the array values as the map key so identical colours merge.
      const key = Array.isArray(colour) ? colour.join(",") : String(colour);
      if (sliceMap.has(key)) {
        sliceMap.get(key).count += 1;
      } else {
        sliceMap.set(key, { colour, count: 1 });
      }
    }

    const slices = Array.from(sliceMap.values());
    totalLeaves += numberOfActiveLeaves;

    return { node, numberOfActiveLeaves, slices };
  });

  // Compute per-node ratio (relative size scaling: log10-normalised leaf count).
  const k =
    totalLeaves > 1 && Math.log10(totalLeaves) !== 0
      ? 1 / Math.log10(totalLeaves)
      : 0;

  for (const entry of nodeData) {
    entry.ratio = k * Math.log10(Math.max(1, entry.numberOfActiveLeaves));
  }

  return nodeData;
}

// ---------------------------------------------------------------------------
// Sector path builder
// Angle convention: 0 = top, clockwise.
//   x = cx + R * sin(a)
//   y = cy - R * cos(a)
// ---------------------------------------------------------------------------

function sectorPoint(cx, cy, R, angle) {
  return [cx + R * Math.sin(angle), cy - R * Math.cos(angle)];
}

function buildSlicePaths(node, nodeData, nodeSize, strokeAttrs) {
  const { numberOfActiveLeaves, ratio, slices } = nodeData;
  const baseRadius = (nodeSize ?? 14) / 2;
  const R = baseRadius * (1 + ratio);

  const cx = node.x;
  const cy = node.y;

  const paths = [];
  let startAngle = 0;

  // Edge case: single colour fills the whole disc — emit a <circle>.
  if (slices.length === 1) {
    const fill = rgba(slices[0].colour, false);
    paths.push(
      el("circle", {
        cx,
        cy,
        r: R,
        fill,
        ...strokeAttrs,
      })
    );
    return paths;
  }

  for (const slice of slices) {
    const sweep = TWO_PI * (slice.count / numberOfActiveLeaves);
    const endAngle = startAngle + sweep;

    const [x1, y1] = sectorPoint(cx, cy, R, startAngle);
    const [x2, y2] = sectorPoint(cx, cy, R, endAngle);
    const largeArcFlag = sweep > Math.PI ? 1 : 0;

    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    paths.push(
      el("path", {
        d,
        fill: rgba(slice.colour, false),
        ...strokeAttrs,
      })
    );

    startAngle = endAngle;
  }

  return paths;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render piechart discs for every collapsed internal node in the graph.
 *
 * @param {object} graph - Built graph (from buildGraph + all three passes).
 * @param {object} props - Merged props object.
 * @returns {SVGGElement|null}
 */
export function renderPiecharts(graph, props) {
  if (props.showPiecharts === false) return null;

  // Collect visible collapsed internal nodes.
  const targetNodes = [];
  for (const node of graph.preorderTraversal) {
    if (node.isCollapsed && !node.isHidden && !node.isLeaf) {
      targetNodes.push(node);
    }
  }

  if (targetNodes.length === 0) return null;

  // Build slice data for all target nodes (ratio needs totalLeaves across all).
  const allNodeData = buildPieData(targetNodes);

  // Resolve stroke attributes once for all slices.
  let strokeAttrs;
  if (props.showShapeBorders) {
    const rawColour = props.strokeColour ?? [34, 34, 34, 255];
    const [r, g, b] = rawColour;
    const a = Math.round((props.shapeBorderAlpha ?? 0.28) * 255);
    strokeAttrs = {
      stroke: rgba([r, g, b, a], false),
      "stroke-width": props.shapeBorderWidth ?? 1,
    };
  } else {
    strokeAttrs = { stroke: "none" };
  }

  const nodeSize = effectiveNodeSize(props);

  const root = el("g", { class: "piecharts" });

  for (const entry of allNodeData) {
    const { node } = entry;
    const pieGroup = el("g", {
      class: "pie",
      "data-node-id": node.id,
    });

    const paths = buildSlicePaths(node, entry, nodeSize, strokeAttrs);
    for (const path of paths) {
      pieGroup.appendChild(path);
    }

    root.appendChild(pieGroup);
  }

  return root;
}
